
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AuthPageLayout from '@/components/auth/AuthPageLayout';

export default function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al enviar las instrucciones');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthPageLayout
      leftIcon="lock_reset"
      leftTitle={
        <>
          Recupera tu<br />acceso
        </>
      }
      leftSubtitle="Te enviaremos instrucciones a tu correo para restablecer tu contraseña de forma segura."
    >
      <div className="absolute top-6 left-6 z-20">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400 hover:text-primary transition-colors font-medium"
        >
          <span className="material-icons text-sm">arrow_back</span>
          Volver al inicio de sesión
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center p-4 sm:p-12 lg:p-24">
        <div className="w-full max-w-md space-y-8">
          <div className="flex md:hidden justify-center mb-6">
            <div className="flex items-center gap-2">
              <span className="material-icons text-primary text-3xl">account_balance_wallet</span>
              <span className="font-bold text-2xl tracking-tight text-gray-900 dark:text-white">FLUX</span>
            </div>
          </div>

          <div className="text-left">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center md:hidden">
                <span className="material-icons text-primary text-xl">lock_reset</span>
              </div>
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                ¿Olvidaste tu contraseña?
              </h2>
            </div>
            <p className="text-slate-500 dark:text-gray-400 mt-2">
              Ingresa tu correo y te enviaremos instrucciones para restablecerla.
            </p>
          </div>

          {sent ? (
            <div className="space-y-6">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl px-5 py-6 text-center">
                <div className="flex justify-center mb-3">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center">
                    <span className="material-icons text-green-600 dark:text-green-400 text-2xl">mark_email_read</span>
                  </div>
                </div>
                <p className="text-sm font-semibold text-green-800 dark:text-green-300 mb-1">
                  Instrucciones enviadas
                </p>
                <p className="text-sm text-green-700 dark:text-green-400">
                  Revisa tu bandeja de entrada en{' '}
                  <span className="font-semibold">{email}</span> y sigue el enlace para restablecer tu contraseña.
                </p>
              </div>
              <Link
                to="/login"
                className="block w-full text-center py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-white bg-white dark:bg-surface-dark hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Volver al inicio de sesión
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
                  <span className="material-icons text-base">error</span>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Correo electrónico
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="material-symbols-outlined text-gray-400 text-[20px]">mail</span>
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="nombre@empresa.com"
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-primary focus:border-primary text-sm dark:bg-surface-dark dark:text-white placeholder-gray-400 transition-colors"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-xl text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-lg shadow-blue-500/30 transition-all transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Enviando...' : 'Enviar instrucciones'}
                  <span className="absolute right-4 flex items-center">
                    <span className="material-icons text-blue-200 group-hover:text-white transition-colors text-sm">
                      arrow_forward
                    </span>
                  </span>
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </AuthPageLayout>
  );
}
